import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { assistantApi } from '../../auth/api';
import { useAuth } from '../../auth/AuthContext';
import {
  generateLocalAnswer,
  parseOutreachDraft,
  escapeHtml,
  renderMarkdown,
  SUGGESTED_QUESTIONS,
} from '../../utils/assistant';
import { readAssistantSse } from '../../utils/assistantSse';
import { buildAssistantContext } from '../../utils/assistantContext';
import { track, trackException } from '../../lib/track';
import { useAssistantPageExtras } from './AssistantContext';
import { assistantHiPrefix } from './assistantHiPrefix';
import {
  ASSISTANT_TABS,
  EXPLAIN_STUB,
  REPORT_STUB,
  applyServerLane,
  channelFromStored,
  chipsForPage,
  guideForPage,
  historyForIntent,
  intentForTab,
  laneForComposerTurn,
  laneFromServer,
  nextTurnAfterTabSwitch,
  splitGuideLink,
  stoppedTurnAfterDetach,
  tabAfterServerLane,
} from './assistantTabs';
import {
  clearLiveThread,
  liveThreadOwner,
  inFlightLiveSnapshot,
  loadLiveThread,
  mergeHydratedMessages,
  saveLiveThread,
  stoppedTurnAppendBody,
} from './assistantLiveThread';

const LOGO = `${import.meta.env.BASE_URL}d2d-energy-mark.svg`;
const CRM_APPLY_EVENT = 'crm-apply-outreach';
const CRM_ASK_EVENT = 'crm-ask-assistant';

function formatWhen(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/** Coarse topic tags only — never store full question text (CA). */
function topicTagsFromQuestion(text) {
  const q = String(text || '').toLowerCase();
  const tags = [];
  if (/\b(bid|irr|moic|npv|pv10|underwrit)/.test(q)) tags.push('economics');
  if (/\b(overhead|loe|g&a|gna|opex)/.test(q)) tags.push('overhead');
  if (/\b(boswell|decline|dca|production|l3|l12|first.?prod)/.test(q)) tags.push('production');
  if (/\b(log|las|evidence|raster|pdf)/.test(q)) tags.push('evidence');
  if (/\b(liabilit|plug|aro|p&a|inactive)/.test(q)) tags.push('liability');
  if (/\b(crm|investor|capital|lp)\b/.test(q)) tags.push('crm');
  return tags.slice(0, 4);
}

/**
 * D2D Energy assistant — Claude Opus via authenticated /api proxy,
 * with local engine fallback if the API is unavailable.
 * Personal Agent P0: one agent per (org, user); session mint + SSE stream
 * persist/hydrate. Same APIs for Data Room (VDR) and ABS desk.
 */
export default function AssistantPanel({
  dashboardKey,
  dashboardLabel,
  pageData,
  dealPackage,
  executive,
  selectionContext = null,
}) {
  const { user, isAdmin } = useAuth();
  const hi = assistantHiPrefix(user);
  const { extras } = useAssistantPageExtras();
  const [open, setOpen] = useState(false);
  // Close on navigation so the destination page is fully visible (conversation is kept).
  useEffect(() => {
    const close = () => setOpen(false);
    window.addEventListener('hashchange', close);
    window.addEventListener('popstate', close);
    return () => {
      window.removeEventListener('hashchange', close);
      window.removeEventListener('popstate', close);
    };
  }, []);
  const owner = liveThreadOwner(user);
  const ownerRef = useRef(owner);
  ownerRef.current = owner;
  const restoredThread = useRef(owner ? loadLiveThread(owner) : null);
  const [messages, setMessages] = useState(() => restoredThread.current?.messages || []);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [engine, setEngine] = useState('claude');
  const [agentId, setAgentId] = useState(null);
  const [threadId, setThreadId] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [conversationTitle, setConversationTitle] = useState(null);
  const conversationIdRef = useRef(null);
  const conversationTitleRef = useRef(null);
  const dashboardKeyRef = useRef(dashboardKey);
  conversationIdRef.current = conversationId;
  conversationTitleRef.current = conversationTitle;
  dashboardKeyRef.current = dashboardKey;
  const [readOnly, setReadOnly] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyError, setHistoryError] = useState(null);
  const [sessionReady, setSessionReady] = useState(false);
  const [tab, setTab] = useState('ask');
  const [guides, setGuides] = useState([]);
  const [guidesLoaded, setGuidesLoaded] = useState(false);
  const [guidesError, setGuidesError] = useState(null);
  const [focusedGuide, setFocusedGuide] = useState(null);
  const [guideBody, setGuideBody] = useState('');
  const [stubPrompt, setStubPrompt] = useState('');
  const [thinkingChannel, setThinkingChannel] = useState('ask');
  const bodyRef = useRef(null);
  const askRef = useRef(null);
  const sessionReadyRef = useRef(false);
  const threadIdRef = useRef(null);
  const agentIdRef = useRef(null);
  const messagesRef = useRef([]);
  const turnInFlightRef = useRef(false);
  const hydrateGenRef = useRef(0);
  const sessionPromiseRef = useRef(null);
  const guideRequestRef = useRef(0);
  const tabRef = useRef('ask');
  const streamAbortRef = useRef(null);
  /** Set when a tab switch aborts the in-flight stream, so the catch can keep a reply. */
  const detachAbortRef = useRef(false);
  const stoppedCommittedRef = useRef(false);
  const inflightQuestionRef = useRef('');
  const inflightPartialRef = useRef('');
  const inflightChannelRef = useRef('ask');
  const turnGenRef = useRef(0);
  const pinnedDuringTurnRef = useRef(false);
  const firmName = extras?.firm?.firm_name;

  function commitTab(next) {
    tabRef.current = next;
    setTab(next);
  }

  function rememberConversation(id, title) {
    conversationIdRef.current = id;
    setConversationId(id);
    if (title !== undefined) {
      conversationTitleRef.current = title ?? null;
      setConversationTitle(title ?? null);
    }
  }

  function persistLiveThread(snapshot) {
    const current = ownerRef.current;
    if (!current) return null;
    return saveLiveThread(current, snapshot);
  }

  messagesRef.current = messages;

  useEffect(() => {
    if (!messages.length) return;
    persistLiveThread({
      messages,
      inFlight: turnInFlightRef.current && !stoppedCommittedRef.current,
      question: inflightQuestionRef.current,
      partial: inflightPartialRef.current,
      channel: inflightChannelRef.current,
      tab,
    });
  }, [messages, tab]);
  const suggestions = useMemo(() => {
    const base = SUGGESTED_QUESTIONS[dashboardKey] || SUGGESTED_QUESTIONS.decision;
    if (dashboardKey !== 'crm' || !firmName) return base;
    return [
      `Research ${firmName} — wealth origin, energy appetite, and a Firebrand hook.`,
      `Draft a personalized teaser email for ${firmName}${extras?.contact?.name ? ` / ${extras.contact.name}` : ''}.`,
      'Review my outreach draft — tighten specificity and CA hygiene.',
      'What should we ask for as the next step (call vs portal invite)?',
    ];
  }, [dashboardKey, firmName, extras?.contact?.name]);
  const chips = useMemo(
    () => chipsForPage(dashboardKey, suggestions),
    [dashboardKey, suggestions],
  );

  const context = useMemo(
    () =>
      buildAssistantContext({
        dashboardKey,
        dashboardLabel,
        pageData,
        dealPackage,
        executive,
        livePageState: extras,
        selectionContext,
      }),
    [dashboardKey, dashboardLabel, pageData, dealPackage, executive, extras, selectionContext],
  );

  const contextReady = Boolean(
    pageData || extras || dashboardKey === 'admin' || dashboardKey === 'crm' || dealPackage,
  );

  function applyOutreachFromMessage(content) {
    const parsed = parseOutreachDraft(content);
    if (!parsed) return;
    window.dispatchEvent(new CustomEvent(CRM_APPLY_EVENT, { detail: parsed }));
    track('crm_assistant_apply_outreach', {
      firm_id: extras?.firm?.id || null,
      subject_len: parsed.subject.length,
      body_len: parsed.body.length,
    });
  }

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, thinking, historyOpen]);

  const markSessionReady = useCallback((ready) => {
    sessionReadyRef.current = ready;
    setSessionReady(ready);
  }, []);

  const hydrateThread = useCallback(
    async (id, gen) => {
      if (!id) {
        if (gen === hydrateGenRef.current) markSessionReady(true);
        return;
      }
      try {
        const res = await assistantApi.getThreadMessages(id);
        // Ignore stale hydrate that would clobber an in-flight turn or a newer mint.
        if (gen !== hydrateGenRef.current || turnInFlightRef.current) return;
        const nextThreadId = res.threadId || id;
        threadIdRef.current = nextThreadId;
        setThreadId(nextThreadId);
        if (res.agentId) {
          agentIdRef.current = res.agentId;
          setAgentId(res.agentId);
        }
        const mapped = (res.messages || []).map((m) => ({
          role: m.role,
          content: m.content,
          meta: m.model || m.costClass || null,
          channel: channelFromStored(m.tab),
        }));
        const merged = mergeHydratedMessages(mapped, messagesRef.current);
        messagesRef.current = merged;
        setMessages(merged);
        persistLiveThread({
          messages: merged,
          inFlight: false,
          question: inflightQuestionRef.current,
          partial: inflightPartialRef.current,
          channel: inflightChannelRef.current,
          tab: tabRef.current,
        });
        markSessionReady(true);
      } catch (err) {
        console.warn('Could not hydrate personal-agent thread', err);
        if (gen === hydrateGenRef.current && !turnInFlightRef.current) {
          markSessionReady(true);
        }
      }
    },
    [markSessionReady],
  );

  const ensureSession = useCallback(async () => {
    if (sessionPromiseRef.current) return sessionPromiseRef.current;
    const gen = ++hydrateGenRef.current;
    const pending = (async () => {
      try {
        const session = await assistantApi.createSession({
          surface: dashboardKey,
          title: dashboardLabel ? `${dashboardLabel}` : undefined,
        });
        if (gen !== hydrateGenRef.current) return null;
        agentIdRef.current = session.agentId;
        threadIdRef.current = session.threadId;
        setAgentId(session.agentId);
        setThreadId(session.threadId);
        // Reconnect = hydrate existing messages, then new stream on ask.
        await hydrateThread(session.threadId, gen);
        if (gen === hydrateGenRef.current) {
          track('assistant_session_ready', {
            dashboard: dashboardKey,
            agent_id: session.agentId,
            thread_id: session.threadId,
          });
        }
        return session;
      } catch (err) {
        console.warn('Personal-agent session mint failed', err);
        if (gen === hydrateGenRef.current && !turnInFlightRef.current) {
          markSessionReady(true);
        }
        return null;
      }
    })();
    sessionPromiseRef.current = pending;
    try {
      return await pending;
    } finally {
      if (sessionPromiseRef.current === pending) sessionPromiseRef.current = null;
    }
  }, [dashboardKey, dashboardLabel, hydrateThread, markSessionReady]);

  useEffect(() => {
    if (!open || !user) return undefined;
    // Continuity: every open hydrates the personal-agent thread (reload < episodic TTL).
    markSessionReady(false);
    sessionPromiseRef.current = null;
    ensureSession();
    return undefined;
  }, [open, user, ensureSession, markSessionReady]);

  useEffect(() => {
    if (!open || !user) return undefined;
    let cancel = false;
    setGuidesLoaded(false);
    assistantApi
      .listGuides()
      .then((res) => {
        if (cancel) return;
        setGuides(Array.isArray(res?.guides) ? res.guides : []);
        setGuidesError(null);
        setGuidesLoaded(true);
      })
      .catch((err) => {
        if (cancel) return;
        setGuides([]);
        setGuidesError(err?.message || 'Guides could not be loaded');
        setGuidesLoaded(true);
      });
    return () => {
      cancel = true;
    };
  }, [open, user]);

  async function openGuide(id) {
    if (!id) return;
    const requestId = guideRequestRef.current + 1;
    guideRequestRef.current = requestId;
    setGuidesError(null);
    try {
      const detail = await assistantApi.getGuide(id);
      if (guideRequestRef.current !== requestId) return;
      setFocusedGuide(detail);
      setGuideBody(typeof detail?.body === 'string' ? detail.body : '');
    } catch (err) {
      if (guideRequestRef.current !== requestId) return;
      setFocusedGuide(null);
      setGuideBody('');
      setGuidesError(err?.status === 404 ? 'That guide is not available.' : err?.message || 'Guide unavailable');
    }
  }

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const res = await assistantApi.listConversations();
      setHistory(res.conversations || []);
    } catch (err) {
      setHistoryError(err.message || 'Could not load history');
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && historyOpen) loadHistory();
  }, [open, historyOpen, loadHistory]);

  // CRM detail pane can open the fab and queue a research / draft / review prompt.
  useEffect(() => {
    if (dashboardKey !== 'crm') return undefined;
    const onAsk = (ev) => {
      const prompt = (ev?.detail?.prompt || '').trim();
      if (!prompt) return;
      setOpen(true);
      commitTab('ask');
      setHistoryOpen(false);
      window.setTimeout(() => askRef.current?.(prompt, { intent: 'ask' }), 40);
    };
    window.addEventListener(CRM_ASK_EVENT, onAsk);
    return () => window.removeEventListener(CRM_ASK_EVENT, onAsk);
  }, [dashboardKey]);

  function startNewChat() {
    clearLiveThread(ownerRef.current);
    stoppedCommittedRef.current = false;
    inflightQuestionRef.current = '';
    inflightPartialRef.current = '';
    setMessages([]);
    messagesRef.current = [];
    setInput('');
    rememberConversation(null, null);
    setReadOnly(false);
    setHistoryOpen(false);
    markSessionReady(false);
    threadIdRef.current = null;
    sessionPromiseRef.current = null;
    hydrateGenRef.current += 1;
    const gen = hydrateGenRef.current;
    track('assistant_new_chat', { dashboard: dashboardKey, agent_id: agentIdRef.current });
    (async () => {
      try {
        const session = await assistantApi.createSession({
          surface: dashboardKey,
          force_new_thread: true,
        });
        if (gen !== hydrateGenRef.current || turnInFlightRef.current) return;
        agentIdRef.current = session.agentId;
        threadIdRef.current = session.threadId;
        setAgentId(session.agentId);
        setThreadId(session.threadId);
        messagesRef.current = [];
        setMessages([]);
        markSessionReady(true);
      } catch (err) {
        console.warn('Could not start new personal-agent thread', err);
        if (gen === hydrateGenRef.current) markSessionReady(true);
      }
    })();
  }

  async function openConversation(id) {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const conv = await assistantApi.getConversation(id);
      rememberConversation(conv.id, conv.title);
      setReadOnly(!conv.mine);
      const mapped = (conv.messages || []).map((m) => ({
        role: m.role,
        content: m.content,
        meta: typeof m.meta === 'object' ? m.meta?.engine : m.meta,
        channel: channelFromStored(typeof m.meta === 'object' ? m.meta?.channel : null),
      }));
      messagesRef.current = mapped;
      setMessages(mapped);
      setHistoryOpen(false);
      track('assistant_history_opened', { conversation_id: id, mine: conv.mine });
    } catch (err) {
      setHistoryError(err.message || 'Could not open conversation');
    } finally {
      setHistoryLoading(false);
    }
  }

  async function deleteConversation(id, e) {
    e?.stopPropagation?.();
    if (!window.confirm('Delete this conversation permanently?')) return;
    try {
      await assistantApi.deleteConversation(id);
      setHistory((h) => h.filter((c) => c.id !== id));
      if (conversationId === id) startNewChat();
      track('assistant_history_deleted', { conversation_id: id });
    } catch (err) {
      setHistoryError(err.message || 'Delete failed');
      trackException(err, { context: 'assistant_history_delete' });
    }
  }

  async function ask(text, options = {}) {
    const myGen = turnGenRef.current;
    pinnedDuringTurnRef.current = false;
    const intent = options.intent || intentForTab(tabRef.current);
    const guideId = options.guideId || (intent === 'how_it_works' ? focusedGuide?.id : null);
    const channel = intent === 'how_it_works' ? 'guides' : 'ask';
    if (!text.trim() || turnInFlightRef.current || readOnly) return;

    // Await session before ask so history + threadId are bound.
    let activeThreadId = threadIdRef.current;
    if (!sessionReadyRef.current || !activeThreadId) {
      const session = await ensureSession();
      activeThreadId = session?.threadId || threadIdRef.current;
    }
    if (turnInFlightRef.current || readOnly) return;

    const userMsg = { role: 'user', content: text.trim(), channel };
    const prior = [...messagesRef.current, userMsg];
    messagesRef.current = prior;
    setMessages(prior);
    setInput('');
    stoppedCommittedRef.current = false;
    inflightQuestionRef.current = userMsg.content;
    inflightPartialRef.current = '';
    inflightChannelRef.current = channel;
    persistLiveThread({
      messages: prior,
      inFlight: true,
      question: userMsg.content,
      partial: '',
      channel,
      tab: tabRef.current,
    });
    turnInFlightRef.current = true;
    setThinking(true);
    setThinkingChannel(channel);
    track('assistant_question_asked', {
      dashboard: dashboardKey,
      question_len: userMsg.content.length,
      has_page: !!pageData,
      has_live: !!extras,
      conversation_id: conversationId,
      thread_id: activeThreadId,
      agent_id: agentIdRef.current,
      topic_tags: topicTagsFromQuestion(userMsg.content),
      intent,
    });

    let answerText = '';
    let guideForTurn = null;
    const controller = new AbortController();
    streamAbortRef.current = controller;
    const commitStopped = () => {
      if (!detachAbortRef.current || stoppedCommittedRef.current) {
        detachAbortRef.current = false;
        return;
      }
      detachAbortRef.current = false;
      inflightPartialRef.current = answerText;
      freezeInFlightTurn();
    };
    try {
      if (!activeThreadId) {
        const session = await assistantApi.createSession({ surface: dashboardKey });
        activeThreadId = session.threadId;
        agentIdRef.current = session.agentId;
        threadIdRef.current = session.threadId;
        setAgentId(session.agentId);
        setThreadId(session.threadId);
      }

      const res = await assistantApi.stream(
        {
          question: userMsg.content,
          thread_id: activeThreadId,
          dashboard_key: dashboardKey,
          // Guide turns do not send the deal package. The server ignores it too.
          data:
            intent === 'how_it_works'
              ? { activeTab: dashboardKey, label: dashboardLabel }
              : context,
          history: historyForIntent(prior.slice(0, -1), intent),
          intent,
          guide_id: guideId || undefined,
        },
        { signal: controller.signal },
      );

      let doneMeta = null;
      let streamError = null;
      let turnLane = channel;
      await readAssistantSse(res, (frame) => {
        if (myGen !== turnGenRef.current) return;
        // parseSseDataLine already drops unknown types.
        if (frame.type === 'text') {
          turnLane = laneFromServer(frame.tab, turnLane);
          const visibleLane = laneForComposerTurn({ intent, serverLane: turnLane });
          const nextTab = tabAfterServerLane({
            pinned: pinnedDuringTurnRef.current || intent === 'ask',
            currentTab: tabRef.current,
            serverLane: visibleLane,
          });
          if (nextTab !== tabRef.current) commitTab(nextTab);
          setThinkingChannel(visibleLane);
          if (frame.guideId) guideForTurn = frame.guideId;
          answerText += frame.text || '';
          inflightPartialRef.current = answerText;
          inflightChannelRef.current = visibleLane;
          const next = applyServerLane(
            [
              ...prior,
              {
                role: 'assistant',
                content: answerText,
                meta: 'streaming',
                channel: visibleLane,
                guideId: guideForTurn,
              },
            ],
            visibleLane,
          );
          messagesRef.current = next;
          setMessages(next);
          // Sync: a remount can happen before the effect flushes.
          // visibleLane, not the raw server tab, or a stopped Ask reply lands on Guides.
          persistLiveThread(
            inFlightLiveSnapshot({
              messages: next,
              question: inflightQuestionRef.current,
              partial: answerText,
              visibleLane,
              tab: tabRef.current,
            }),
          );
        } else if (frame.type === 'done') {
          doneMeta = frame;
          turnLane = laneFromServer(frame.tab, turnLane);
          if (frame.guideId) guideForTurn = frame.guideId;
        } else if (frame.type === 'error') {
          streamError = frame.message || 'Assistant error';
        }
      });
      if (myGen !== turnGenRef.current) {
        commitStopped();
        return;
      }

      // If any text already streamed, do not take the local-fallback path
      // (persist failure is non-fatal and still ends with done).
      if (streamError && !answerText) {
        throw Object.assign(new Error(streamError), { status: 502 });
      }

      setEngine('claude');
      if (doneMeta?.threadId) {
        threadIdRef.current = doneMeta.threadId;
        setThreadId(doneMeta.threadId);
      }
      if (doneMeta?.agentId) {
        agentIdRef.current = doneMeta.agentId;
        setAgentId(doneMeta.agentId);
      }
      if (!conversationTitleRef.current) {
        rememberConversation(conversationIdRef.current, userMsg.content.slice(0, 80));
      }
      if (answerText) {
        const settled = laneForComposerTurn({
          intent,
          serverLane: laneFromServer(doneMeta?.tab, turnLane),
        });
        if (settled === 'guides' && !pinnedDuringTurnRef.current) commitTab('guides');
        const finalMsgs = applyServerLane(
          [
            ...prior,
            {
              role: 'assistant',
              content: answerText,
              meta: doneMeta?.model || 'claude-opus',
              channel: settled,
              guideId: guideForTurn,
            },
          ],
          settled,
        );
        messagesRef.current = finalMsgs;
        setMessages(finalMsgs);
        track('assistant_answered', {
          dashboard: dashboardKey,
          engine: 'claude',
          thread_id: doneMeta?.threadId || activeThreadId,
          agent_id: doneMeta?.agentId || agentIdRef.current,
          persist_warning: Boolean(streamError),
        });
      } else if (streamError) {
        throw Object.assign(new Error(streamError), { status: 502 });
      }
    } catch (err) {
      if (detachAbortRef.current || err?.name === 'AbortError' || myGen !== turnGenRef.current) {
        commitStopped();
        return;
      }
      // Only fall back when nothing was streamed — never duplicate Claude's answer.
      if (answerText) {
        console.warn('Assistant stream ended with error after text; keeping streamed answer', err);
        trackException(err, {
          context: 'assistant_claude_stream_after_text',
          dashboard: dashboardKey,
        });
      } else if (intent !== 'ask') {
        console.warn('Assistant guide stream failed', err);
        trackException(err, { context: 'assistant_guide_stream', dashboard: dashboardKey });
        const fullAnswer =
          'The guide answer did not load. Open the guide in this panel and follow the steps, or try again.';
        const next = [...prior, { role: 'assistant', content: fullAnswer, meta: 'local', channel }];
        messagesRef.current = next;
        setMessages(next);
        track('assistant_answered', {
          dashboard: dashboardKey,
          engine: 'local',
          fallback: true,
          intent,
        });
      } else {
        console.warn('Claude assistant stream failed, using local engine', err);
        trackException(err, { context: 'assistant_claude_stream', dashboard: dashboardKey });
        setEngine('local');
        const answer = generateLocalAnswer({
          dashboardKey,
          data: pageData,
          context,
          question: userMsg.content,
        });
        const note =
          err.status === 401 || err.status === 403
            ? '_Sign in required for the full assistant — showing local answer._\n\n'
            : '_Assistant service unavailable — showing local grounded answer._\n\n';
        const fullAnswer = note + answer;
        const next = [...prior, { role: 'assistant', content: fullAnswer, meta: 'local', channel }];
        messagesRef.current = next;
        setMessages(next);
        track('assistant_answered', { dashboard: dashboardKey, engine: 'local', fallback: true });
        try {
          const saved = await assistantApi.appendTurn({
            question: userMsg.content,
            answer: fullAnswer,
            dashboard_key: dashboardKey,
            conversation_id: conversationId,
            assistant_meta: 'local',
            channel,
          });
          if (saved?.conversation?.id) {
            rememberConversation(
              saved.conversation.id,
              saved.conversation.title || conversationTitleRef.current,
            );
          }
        } catch (persistErr) {
          console.warn('Could not persist local assistant turn', persistErr);
        }
      }
    } finally {
      if (streamAbortRef.current === controller) {
        streamAbortRef.current = null;
        turnInFlightRef.current = false;
        setThinking(false);
      }
    }
  }
  askRef.current = ask;

  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      ask(input);
    }
  }

  function freezeInFlightTurn() {
    if (stoppedCommittedRef.current) return;
    stoppedCommittedRef.current = true;
    const channel = inflightChannelRef.current === 'guides' ? 'guides' : 'ask';
    const question = inflightQuestionRef.current;
    const stopped = stoppedTurnAfterDetach({
      question,
      partial: inflightPartialRef.current,
    });
    const kept = messagesRef.current.filter((message) => message.meta !== 'streaming');
    const next = [...kept, { ...stopped, channel }];
    messagesRef.current = next;
    setMessages(next);
    turnInFlightRef.current = false;
    persistLiveThread({
      messages: next,
      inFlight: false,
      question,
      partial: '',
      channel,
      tab: 'ask',
    });
    assistantApi
      .appendTurn({
        ...stoppedTurnAppendBody({
          question,
          answer: stopped.content,
          dashboardKey: dashboardKeyRef.current,
          conversationId: conversationIdRef.current,
          channel,
        }),
        assistant_meta: 'stopped',
      })
      .then((saved) => {
        if (saved?.conversation?.id) {
          rememberConversation(
            saved.conversation.id,
            saved.conversation.title || conversationTitleRef.current,
          );
        }
      })
      .catch((persistErr) => {
        console.warn('Could not persist stopped assistant turn', persistErr);
      });
  }

  // Deals list and a deal are different panel instances. Freeze before unmount
  // so the next instance reads a stopped turn, not an empty greeting.
  useEffect(() => {
    return () => {
      if (!turnInFlightRef.current || stoppedCommittedRef.current) return;
      detachAbortRef.current = true;
      freezeInFlightTurn();
      streamAbortRef.current?.abort();
    };
  }, []);

  function selectTab(next) {
    const detached = nextTurnAfterTabSwitch({
      inFlight: turnInFlightRef.current && tabRef.current !== next,
      nextTab: next,
    });
    commitTab(detached.tab);
    setHistoryOpen(false);
    if (!detached.detachStream) return;
    pinnedDuringTurnRef.current = true;
    detachAbortRef.current = true;
    freezeInFlightTurn();
    turnGenRef.current += 1;
    streamAbortRef.current?.abort();
  }

  function onChip(chip) {
    track('assistant_intent', { dashboard: dashboardKey, intent: chip.intent });
    if (chip.tab === 'explain' || chip.tab === 'report') {
      setStubPrompt(chip.text);
      selectTab(chip.tab);
      return;
    }
    if (chip.intent === 'how_it_works') {
      const match = guideForPage(guides, dashboardKey);
      selectTab('guides');
      if (match?.id) openGuide(match.id);
      ask(chip.text, { intent: 'how_it_works', guideId: match?.id || null });
      return;
    }
    selectTab('ask');
    ask(chip.text, { intent: 'ask' });
  }

  const pageGuides = guides.filter((guide) => (guide.pages || [guide.page]).includes(dashboardKey));
  const pageGuideIds = new Set(pageGuides.map((guide) => guide.id));
  const otherGuides = guides.filter((guide) => !pageGuideIds.has(guide.id));
  const visibleMessages = messages.filter((message) => (message.channel || 'ask') === (tab === 'guides' ? 'guides' : 'ask'));
  const showComposer = tab === 'ask' || tab === 'guides';

  if (!open) {
    return (
      <button
        type="button"
        className="assistant-fab"
        aria-label="D2D Energy Assistant"
        onClick={() => {
          setOpen(true);
          track('assistant_opened', { dashboard: dashboardKey });
        }}
        title="D2D Energy Assistant"
      >
        <img src={LOGO} alt="" className="assistant-fab-logo" />
      </button>
    );
  }

  return (
    <div className="assistant-panel" role="dialog" aria-label="D2D Energy Assistant">
      <div className="assistant-header">
        <div className="assistant-header-left">
          <img src={LOGO} alt="D2D Energy" className="assistant-avatar-img" />
          <div>
            <div className="assistant-title">D2D Energy Assistant</div>
            <div className="assistant-subtitle">
              {conversationTitle ? `${conversationTitle} · ` : ''}
              {dashboardLabel}
              {extras ? ' · live knobs' : ''}
              {' · '}
              {engine === 'claude' ? 'D2D Assistant' : 'D2D Assistant (offline mode)'}
              {readOnly ? ' · view only' : ''}
            </div>
          </div>
        </div>
        <div className="assistant-header-actions">
          <button
            type="button"
            className={`assistant-icon-btn${historyOpen ? ' active' : ''}`}
            onClick={() => setHistoryOpen((v) => !v)}
            title="Past conversations"
            aria-label="Past conversations"
            aria-pressed={historyOpen}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
              <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
          <button
            type="button"
            className="assistant-icon-btn"
            onClick={startNewChat}
            title="New chat"
            aria-label="New chat"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
          <button type="button" className="assistant-close" onClick={() => setOpen(false)} title="Close">
            ×
          </button>
        </div>
      </div>

      <div className="assistant-tabs" role="tablist" aria-label="Assistant sections">
        {ASSISTANT_TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            id={`assistant-tab-${item.id}`}
            className={`assistant-tab${tab === item.id ? ' active' : ''}`}
            aria-selected={tab === item.id}
            aria-controls={`assistant-panel-${item.id}`}
            onClick={() => selectTab(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {historyOpen ? (
        <div className="assistant-history">
          <div className="assistant-history-head">
            <span>Past conversations</span>
            <button type="button" className="assistant-history-refresh" onClick={loadHistory}>
              Refresh
            </button>
          </div>
          {isAdmin ? (
            <div className="assistant-history-note">Admins see all chats; delete own or any.</div>
          ) : (
            <div className="assistant-history-note">Your chats — you can delete any you started.</div>
          )}
          {historyLoading && <div className="assistant-history-empty">Loading…</div>}
          {historyError && <div className="assistant-history-error">{historyError}</div>}
          {!historyLoading && !history.length && !historyError && (
            <div className="assistant-history-empty">No saved conversations yet.</div>
          )}
          <ul className="assistant-history-list">
            {history.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  className={`assistant-history-item${c.id === conversationId ? ' current' : ''}`}
                  onClick={() => openConversation(c.id)}
                >
                  <span className="assistant-history-title">{c.title}</span>
                  <span className="assistant-history-meta">
                    {formatWhen(c.updated_at)}
                    {c.dashboard_key ? ` · ${c.dashboard_key}` : ''}
                    {isAdmin && !c.mine && c.owner_email ? ` · ${c.owner_email}` : ''}
                    {` · ${c.message_count || 0} msgs`}
                  </span>
                </button>
                {c.can_delete ? (
                  <button
                    type="button"
                    className="assistant-history-delete"
                    title="Delete conversation"
                    aria-label="Delete conversation"
                    onClick={(e) => deleteConversation(c.id, e)}
                  >
                    ×
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : tab === 'explain' || tab === 'report' ? (
        <div
          className="assistant-stub"
          id={`assistant-panel-${tab}`}
          role="tabpanel"
          aria-labelledby={`assistant-tab-${tab}`}
        >
          <div className="assistant-msg assistant">
            <div className="assistant-msg-bubble">
              <strong>{tab === 'explain' ? 'Explain' : 'Report a problem or suggest an idea'}</strong>
              <br />
              {tab === 'explain' ? EXPLAIN_STUB : REPORT_STUB}
              {stubPrompt ? (
                <>
                  <br />
                  <br />
                  You asked: {stubPrompt}
                </>
              ) : null}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div
            className="assistant-body"
            id={`assistant-panel-${tab}`}
            role="tabpanel"
            aria-labelledby={`assistant-tab-${tab}`}
            ref={bodyRef}
          >
            {tab === 'guides' && (
              <div className="assistant-guides">
                {focusedGuide ? (
                  <>
                    <div className="assistant-guide-focus">Using guide: {focusedGuide.title}</div>
                    <button
                      type="button"
                      className="assistant-guide-back"
                      onClick={() => {
                        setFocusedGuide(null);
                        setGuideBody('');
                      }}
                    >
                      Back to guides
                    </button>
                    <div
                      className="assistant-guide-body"
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(guideBody || '') }}
                    />
                    {guidesError ? <div className="assistant-history-error">{guidesError}</div> : null}
                  </>
                ) : (
                  <>
                    <div className="assistant-suggestions-label">On this page</div>
                    {pageGuides.map((guide) => (
                      <button
                        key={guide.id}
                        type="button"
                        className="assistant-suggestion"
                        onClick={() => openGuide(guide.id)}
                      >
                        {guide.title}
                      </button>
                    ))}
                    {!guidesLoaded && !guidesError ? (
                      <div className="assistant-history-empty">Loading guides…</div>
                    ) : null}
                    {guidesLoaded && !pageGuides.length && !guidesError ? (
                      <div className="assistant-history-empty">No guide is filed for this page yet.</div>
                    ) : null}
                    {otherGuides.length ? (
                      <div className="assistant-suggestions-label">Other guides</div>
                    ) : null}
                    {otherGuides.map((guide) => (
                      <button
                        key={guide.id}
                        type="button"
                        className="assistant-suggestion"
                        onClick={() => openGuide(guide.id)}
                      >
                        {guide.title}
                      </button>
                    ))}
                    {guidesError ? <div className="assistant-history-error">{guidesError}</div> : null}
                  </>
                )}
              </div>
            )}
            {tab === 'ask' && visibleMessages.length === 0 && (
              <>
                <div className="assistant-msg assistant">
                  <div className="assistant-msg-bubble">
                    {dashboardKey === 'crm' ? (
                      <>
                        <strong>
                          {hi} — I&apos;m your investor CRM agent.
                        </strong>{' '}
                        I can research the
                        selected firm (web + FINTRX fields), draft personalized teaser email, and
                        review outreach for specificity / CA hygiene.
                        {firmName ? (
                          <>
                            {' '}
                            Focused on <em>{firmName}</em>
                            {extras?.contact?.name ? ` · ${extras.contact.name}` : ''}.
                          </>
                        ) : (
                          <> Select a firm in the list to ground research and drafts.</>
                        )}
                        {dealPackage ? ' DealPackage context is loaded for safe framing.' : ''}
                      </>
                    ) : dashboardKey === 'projects' ? (
                      <>
                        <strong>
                          {hi} — I&apos;m the D2D Energy desk assistant.
                        </strong>{' '}
                        I can greet you
                        by name, remind you where you left off, and help admins capture VDR portal
                        details for a new project. I will not invent economics — package numbers stay
                        on the deal desks.
                      </>
                    ) : (
                      <>
                        <strong>
                          {hi} — I&apos;m the D2D Energy diligence assistant.
                        </strong>{' '}
                        Context
                        loaded for <em>{dashboardLabel}</em>
                        {pageData ? ' (this tab)' : ''}
                        {dealPackage ? ' + DealPackage engine' : ''}
                        {extras ? ' + your live on-screen knobs' : ''}. Ask about Boswell, bid/IRR,
                        netback, or risks. Past chats are saved — open the clock icon to revisit
                        them.
                      </>
                    )}
                    {!contextReady ? (
                      <>
                        <br />
                        <em>Still loading tab data…</em>
                      </>
                    ) : null}
                  </div>
                </div>
                <div className="assistant-suggestions">
                  <div className="assistant-suggestions-label">Suggested</div>
                  {chips.map((chip) => (
                    <button
                      key={chip.id}
                      type="button"
                      className="assistant-suggestion"
                      onClick={() => onChip(chip)}
                      disabled={!sessionReady || thinking || readOnly}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
              </>
            )}

            {visibleMessages.map((m, i) => {
              const linked = m.role === 'assistant' ? splitGuideLink(m.content) : { text: m.content, guideId: null };
              const guideId = m.guideId || linked.guideId;
              return (
              <div key={i} className={`assistant-msg ${m.role}`}>
                <div
                  className="assistant-msg-bubble"
                  dangerouslySetInnerHTML={{
                    __html: m.role === 'assistant' ? renderMarkdown(linked.text) : escapeHtml(m.content),
                  }}
                />
                {guideId ? (
                  <button
                    type="button"
                    className="assistant-guide-back"
                    onClick={() => {
                      commitTab('guides');
                      openGuide(guideId);
                    }}
                  >
                    Open this guide
                  </button>
                ) : null}
                {m.retryQuestion ? (
                  <button
                    type="button"
                    className="assistant-guide-back"
                    disabled={thinking || readOnly}
                    onClick={() => ask(m.retryQuestion)}
                  >
                    Ask again
                  </button>
                ) : null}
                {dashboardKey === 'crm' &&
                  m.role === 'assistant' &&
                  parseOutreachDraft(m.content) && (
                    <button
                      type="button"
                      className="assistant-insert-outreach"
                      onClick={() => applyOutreachFromMessage(m.content)}
                    >
                      Insert into outreach editor
                    </button>
                  )}
              </div>
              );
            })}

            {thinking && thinkingChannel === (tab === 'guides' ? 'guides' : 'ask') && (
              <div className="assistant-msg assistant">
                <div className="assistant-msg-bubble">
                  <em>
                    {dashboardKey === 'crm'
                      ? 'Researching / drafting…'
                      : 'Thinking…'}
                  </em>
                </div>
              </div>
            )}
          </div>

          {showComposer ? (
          <form
            className="assistant-input-bar"
            onSubmit={(e) => {
              e.preventDefault();
              ask(input);
            }}
          >
            <textarea
              className="assistant-input"
              value={input}
              rows={1}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={
                readOnly
                  ? "View-only (another user's chat) — start a new chat to ask"
                  : !sessionReady
                    ? 'Connecting assistant…'
                    : tab === 'guides'
                      ? focusedGuide
                        ? `Ask about ${focusedGuide.title}…`
                        : 'Ask how this page works…'
                    : dashboardKey === 'crm'
                      ? firmName
                        ? `Ask about ${firmName} research or outreach…`
                        : 'Select a firm, then ask for research or a draft…'
                      : `Ask about ${dashboardLabel}…`
              }
              disabled={thinking || readOnly || !sessionReady}
            />
            <button
              className="assistant-send"
              type="submit"
              disabled={!input.trim() || thinking || readOnly || !sessionReady}
            >
              Send
            </button>
          </form>
          ) : null}
        </>
      )}

      <div className="assistant-footer">
        D2D Energy · grounded in {dashboardLabel}
        {dashboardKey === 'crm' && firmName ? ` · ${firmName}` : ''}
        {extras && dashboardKey !== 'crm' ? ' + live knobs' : ''}
        {dashboardKey === 'crm' ? ' · web research on' : ''}
        {user?.email ? ` · ${user.email}` : ''} · CA-confidential
      </div>
    </div>
  );
}
